import { useState, useEffect, Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import HomePage from './components/HomePage';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import Breadcrumbs from './components/Breadcrumbs';
import { supabase } from './lib/supabase';
import { setupDailyBlogScheduler } from './lib/blogScheduler';

// Lazy-loaded components (not needed for initial home page render)
const Auth = lazy(() => import('./components/Auth'));
const ReportScamForm = lazy(() => import('./components/ReportScamForm'));
const ReportScammerForm = lazy(() => import('./components/ReportScammerForm'));
const InfoPages = lazy(() => import('./components/InfoPages'));
const ScamDetailPage = lazy(() => import('./components/ScamDetailPage'));
const ScammerDetailPage = lazy(() => import('./components/ScammerDetailPage'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ScamCheck = lazy(() => import('./components/ScamCheck'));
const AIScamPosts = lazy(() => import('./components/AIScamPosts'));
const BlogSection = lazy(() => import('./components/BlogSection'));

type PageType = 'home' | 'report-scam' | 'report-scammer' | 'auth' | 'about' | 'how-it-works' | 'terms' | 'privacy' | 'request-review' | 'scam-detail' | 'scammer-detail' | 'profile' | 'admin' | 'scam-check' | 'ai-posts' | 'blog-post-detail' | 'blog' | 'blog-detail';

const PAGE_PATHS: Record<string, string> = {
  'report-scam': '/report-a-scam',
  'report-scammer': '/report-a-scammer',
  'ai-posts': '/scam-checks',
  'blog': '/blog',
  'scam-check': '/scam-check',
  'auth': '/auth',
  'profile': '/profile',
  'admin': '/admin',
  'about': '/about',
  'how-it-works': '/how-it-works',
  'terms': '/terms',
  'privacy': '/privacy',
  'request-review': '/request-review',
};

// Loading fallback for lazy components
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-ash-400">Loading...</div>
    </div>
  );
}

function MainApp() {
  const { user, loading } = useAuth();
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [selectedScamId, setSelectedScamId] = useState<string | null>(null);
  const [selectedScammerId, setSelectedScammerId] = useState<string | null>(null);
  const [selectedBlogPostId, setSelectedBlogPostId] = useState<string | null>(null);
  const [selectedBlogSlug, setSelectedBlogSlug] = useState<string | null>(null);
  const [initialCategory, setInitialCategory] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handleRouteChange = async () => {
      const pathname = window.location.pathname;

      // Exact match routes (no segments) - check these FIRST before segment parsing
      if (pathname === '/report-a-scam') {
        setCurrentPage('report-scam');
        return;
      }
      if (pathname === '/report-a-scammer') {
        setCurrentPage('report-scammer');
        return;
      }
      if (pathname === '/blog') {
        setCurrentPage('blog');
        return;
      }
      if (pathname === '/scam-checks') {
        setCurrentPage('ai-posts');
        return;
      }

      // Segment-based routes
      const segments = pathname.split('/').filter(Boolean);

      if (segments.length === 0 || pathname === '/') {
        setCurrentPage('home');
        return;
      }

      // /scam/{slug} → scam report by slug
      if (segments[0] === 'scam' && segments.length === 2) {
        const slug = segments[1];
        const { data } = await supabase
          .from('scam_reports')
          .select('id')
          .eq('slug', slug)
          .maybeSingle();

        if (data) {
          setSelectedScamId(data.id);
          setCurrentPage('scam-detail');
        }
        return;
      }

      // /scammer/{slug} → scammer report by slug
      if (segments[0] === 'scammer' && segments.length === 2) {
        const slug = segments[1];
        const { data } = await supabase
          .from('scammer_reports')
          .select('id')
          .eq('slug', slug)
          .maybeSingle();

        if (data) {
          setSelectedScammerId(data.id);
          setCurrentPage('scammer-detail');
        }
        return;
      }

      // /blog/{slug} → blog post by slug
      if (segments[0] === 'blog' && segments.length === 2) {
        const slug = segments[1];
        setSelectedBlogSlug(slug);
        setCurrentPage('blog-detail');
        return;
      }

      // /report/{domain}/scam-check → domain scan by domain name
      if (segments[0] === 'report' && segments.length === 3 && segments[2] === 'scam-check') {
        const domainName = segments[1];
        const { data: domainScan } = await supabase
          .from('domain_scans')
          .select('id, slug')
          .eq('domain', domainName)
          .maybeSingle();
        if (domainScan) {
          setSelectedBlogSlug(domainScan.slug);
          setCurrentPage('blog-detail');
          return;
        }
        // fallback: match by slug
        const { data: domainScanBySlug } = await supabase
          .from('domain_scans')
          .select('id, slug')
          .ilike('slug', `%${domainName}%`)
          .maybeSingle();
        if (domainScanBySlug) {
          setSelectedBlogSlug(domainScanBySlug.slug);
          setCurrentPage('blog-detail');
          return;
        }
      }

      // /report/scenario/{slug} → scenario investigation
      if (segments[0] === 'report' && segments[1] === 'scenario' && segments.length === 3) {
        const scenarioSlug = segments[2];
        const { data: scenario } = await supabase
          .from('scenario_investigations')
          .select('id, slug')
          .eq('slug', scenarioSlug)
          .maybeSingle();
        if (scenario) {
          setSelectedBlogSlug(scenario.slug);
          setCurrentPage('blog-detail');
          return;
        }
      }

      // Try scam_checks view (covers legacy, scenario, domain posts)
      const lastSegment = segments[segments.length - 1];
      const { data: scamCheck } = await supabase
        .from('scam_checks')
        .select('id, slug, post_type')
        .eq('slug', lastSegment)
        .maybeSingle();

      if (scamCheck) {
        setSelectedBlogPostId(scamCheck.id);
        setCurrentPage('blog-post-detail');
        return;
      }

      // Try ai_blog_posts
      const { data: blogPost } = await supabase
        .from('ai_blog_posts')
        .select('id, slug')
        .eq('slug', lastSegment)
        .maybeSingle();

      if (blogPost) {
        setSelectedBlogSlug(blogPost.slug);
        setCurrentPage('blog-detail');
        return;
      }

      // Try domain_scans by slug
      const { data: domainScan } = await supabase
        .from('domain_scans')
        .select('id, slug')
        .eq('slug', lastSegment)
        .maybeSingle();

      if (domainScan) {
        setSelectedBlogSlug(domainScan.slug);
        setCurrentPage('blog-detail');
        return;
      }

      // Info pages
      const infoPageMap: Record<string, PageType> = {
        'about': 'about',
        'how-it-works': 'how-it-works',
        'terms': 'terms',
        'privacy': 'privacy',
        'request-review': 'request-review',
      };
      if (infoPageMap[segments[0]]) {
        setCurrentPage(infoPageMap[segments[0]]);
        return;
      }

      // Auth pages
      if (segments[0] === 'auth') {
        setCurrentPage('auth');
        return;
      }
      if (segments[0] === 'profile') {
        setCurrentPage('profile');
        return;
      }
      if (segments[0] === 'admin') {
        setCurrentPage('admin');
        return;
      }
      if (segments[0] === 'scam-check') {
        setCurrentPage('scam-check');
        return;
      }

      // Fallback to home
      setCurrentPage('home');
    };

    handleRouteChange();
    window.addEventListener('popstate', handleRouteChange);

    // Auto-generate daily blog post on app load (pre-checks before calling edge function)
    setupDailyBlogScheduler();

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-900 flex items-center justify-center">
        <div className="text-ash-400">Loading...</div>
      </div>
    );
  }

  const infoPages: PageType[] = ['about', 'how-it-works', 'terms', 'privacy', 'request-review'];

  const handleScamClick = (scamId: string, slug?: string) => {
    setSelectedScamId(scamId);
    setCurrentPage('scam-detail');
    window.history.pushState(null, '', `/scam/${slug || scamId}`);
  };

  const handleScammerClick = (scammerId: string, slug?: string) => {
    setSelectedScammerId(scammerId);
    setCurrentPage('scammer-detail');
    window.history.pushState(null, '', `/scammer/${slug || scammerId}`);
  };

  const handleBlogPostClick = (postId: string, slug?: string) => {
    setSelectedBlogPostId(postId);
    setCurrentPage('blog-post-detail');
    if (slug) {
      window.history.pushState(null, '', `/${slug}`);
    }
  };

  const handleCategoryClick = (category: string) => {
    setInitialCategory(category);
    setCurrentPage('ai-posts');
    window.history.pushState(null, '', '/scam-checks');
  };

  const handleBlogClick = (slug: string) => {
    if (slug) {
      setSelectedBlogSlug(slug);
      setCurrentPage('blog-detail');
      window.history.pushState(null, '', `/blog/${slug}`);
    } else {
      setCurrentPage('blog');
      window.history.pushState(null, '', '/blog');
    }
  };

  const handleBackToHome = () => {
    setSelectedScamId(null);
    setSelectedScammerId(null);
    setSelectedBlogPostId(null);
    setCurrentPage('home');
    window.history.pushState(null, '', '/');
  };

  const breadcrumbItems: { label: string; onClick?: () => void }[] = [];
  if (currentPage !== 'home') {
    breadcrumbItems.push({ label: 'Home', onClick: handleBackToHome });
    const labelMap: Record<string, string> = {
      'report-scam': 'Report Scam',
      'report-scammer': 'Report Scammer',
      auth: 'Sign In',
      about: 'About',
      'how-it-works': 'How It Works',
      terms: 'Terms',
      privacy: 'Privacy',
      'request-review': 'Request Review',
      'scam-detail': 'Scam Report',
      'scammer-detail': 'Scammer Report',
      profile: 'Profile',
      admin: 'Admin',
      'scam-check': 'Is It Scam?',
      'ai-posts': 'Scam Checks',
      'blog-post-detail': 'Scam Check',
      blog: 'Blog',
      'blog-detail': 'Blog Post',
    };
    breadcrumbItems.push({ label: labelMap[currentPage] || currentPage });
  }

  return (
    <div className="min-h-screen bg-ink-900 flex flex-col">
      <Navbar onNavigate={(page) => {
        setCurrentPage(page);
        const path = PAGE_PATHS[page] || '/';
        window.history.pushState(null, '', path);
      }} currentPage={currentPage} />

      {breadcrumbItems.length > 0 && <Breadcrumbs items={breadcrumbItems} />}

      <main className="flex-grow">
        {currentPage === 'home' && (
          <HomePage
            onScamClick={handleScamClick}
            onScammerClick={handleScammerClick}
            onCategoryClick={handleCategoryClick}
            onPostClick={handleBlogPostClick}
            onBlogClick={handleBlogClick}
          />
        )}
        {currentPage === 'auth' && (
          <Suspense fallback={<PageLoader />}>
            <Auth onSuccess={() => setCurrentPage('home')} />
          </Suspense>
        )}
        {currentPage === 'report-scam' && (
          <div className="px-4 sm:px-6 lg:px-8 py-8">
            <Suspense fallback={<PageLoader />}>
              <ReportScamForm
                onSuccess={() => setCurrentPage('home')}
                onNavigateToScam={handleScamClick}
              />
            </Suspense>
          </div>
        )}
        {currentPage === 'report-scammer' && (
          <div className="px-4 sm:px-6 lg:px-8 py-8">
            <Suspense fallback={<PageLoader />}>
              <ReportScammerForm
                onSuccess={() => setCurrentPage('home')}
                onNavigateToScammer={handleScammerClick}
              />
            </Suspense>
          </div>
        )}
        {currentPage === 'scam-check' && (
          <Suspense fallback={<PageLoader />}>
            <ScamCheck />
          </Suspense>
        )}
        {currentPage === 'ai-posts' && (
          <Suspense fallback={<PageLoader />}>
            <AIScamPosts onBack={handleBackToHome} onPostClick={handleBlogPostClick} initialCategory={initialCategory} />
          </Suspense>
        )}
        {currentPage === 'blog-post-detail' && selectedBlogPostId && (
          <Suspense fallback={<PageLoader />}>
            <AIScamPosts postIdToDisplay={selectedBlogPostId} onBack={handleBackToHome} />
          </Suspense>
        )}
        {currentPage === 'blog' && (
          <Suspense fallback={<PageLoader />}>
            <BlogSection onBack={handleBackToHome} />
          </Suspense>
        )}
        {currentPage === 'blog-detail' && selectedBlogSlug && (
          <Suspense fallback={<PageLoader />}>
            <BlogSection slugToDisplay={selectedBlogSlug} onBack={handleBackToHome} />
          </Suspense>
        )}
        {currentPage === 'scam-detail' && selectedScamId && (
          <Suspense fallback={<PageLoader />}>
            <ScamDetailPage reportId={selectedScamId} onBack={handleBackToHome} />
          </Suspense>
        )}
        {currentPage === 'scammer-detail' && selectedScammerId && (
          <Suspense fallback={<PageLoader />}>
            <ScammerDetailPage reportId={selectedScammerId} onBack={handleBackToHome} />
          </Suspense>
        )}
        {currentPage === 'profile' && (
          <div className="px-4 sm:px-6 lg:px-8 py-8">
            <Suspense fallback={<PageLoader />}>
              <UserProfile />
            </Suspense>
          </div>
        )}
        {currentPage === 'admin' && (
          <Suspense fallback={<PageLoader />}>
            <AdminDashboard />
          </Suspense>
        )}
        {infoPages.includes(currentPage) && (
          <Suspense fallback={<PageLoader />}>
            <InfoPages page={currentPage} />
          </Suspense>
        )}
      </main>

      <Footer onNavigate={(page) => {
        setCurrentPage(page as PageType);
        const path = PAGE_PATHS[page] || '/';
        window.history.pushState(null, '', path);
      }} />
      <BackToTop />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;
